import { put, takeLatest, select, delay } from 'redux-saga/effects';
import i18next from 'i18next';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import {
  getIsLoggedIn,
  getIsMobile,
  getIsVerificationNeeded,
} from '../../publicSession/selectors';
import { restrictLoggedOut } from '../../redux/loggedout';
import { scrollIntoViewWithHeader } from '../../utils/scrollIntoViewWithHeader';
import { pushVerificationPopup } from '../../VerificationPopup/actions';
import { pushModal } from '../../Modals/redux/basicActions';
import { ModalType } from '../../Modals/redux/types';
import {
  publishComment,
  publishCommentSuccess,
  publishCommentFailure,
  focusEditor,
  focusEditorWithScroll,
} from './actionCreators';
import {
  CommentPublisher,
  CommentPublisherResult,
  RequestError,
} from './types';
import { sendPublishRequest } from './utils';
import { putErrorNotification } from '../../utils/saga';

function getError(result) {
  if (
    result &&
    result.errorCode !== undefined &&
    RequestError[result.errorCode] !== undefined
  ) {
    return { error: result.errorCode, errorMsg: result.errorDescription };
  }
  return {
    error: RequestError.GeneralError,
    errorMsg: result.errorDescription,
  };
}

export function* publishCommentHandler(action) {
  const {
    editComment,
    replyComment,
    editorId,
    messageId,
    shouldDeleteMessage,
  } = action.payload as CommentPublisher;

  const { isLoggedIn, isVerificationNeeded } = yield select(state => ({
    isLoggedIn: getIsLoggedIn(state),
    isVerificationNeeded: getIsVerificationNeeded(state),
  }));
  if (!isLoggedIn) {
    yield put(
      restrictLoggedOut({ restrictedActionName: ReferralType.COMMENT_ACTION })
    );
    return;
  }

  if (isVerificationNeeded) {
    yield put(pushVerificationPopup('comment'));
    return;
  }

  const result = yield sendPublishRequest(action.payload);

  if (result && !result.error) {
    const publishResult: CommentPublisherResult = {
      editorId,
      comment: result,
      isEdit: !!editComment,
      isReply: !!replyComment,
      messageId,
      shouldDeleteMessage,
    };
    yield put(publishCommentSuccess(publishResult));
  } else {
    let shouldResetEditor = false;
    if (result?.errorCode === 302) {
      window.location.replace(result.errorDetails.redirectUrl);
    } else if (
      result?.errorDetails &&
      result.errorDetails.errorType === 'media_content_violation'
    ) {
      const violations = result.errorDetails.violations ?? [];
      if (violations.length > 0) {
        // The UI spec doesn't cover cases for multiple violations, so we'll just take the first one
        const violation = violations[0];
        if (typeof violation === 'string' && violation === 'spam') {
          // Silently fail but reset/cancel the existing comment
          shouldResetEditor = true;
        } else {
          switch (violation?.mediaType) {
            case 'wix-draft-plugin-giphy':
              yield put(
                pushModal(ModalType.CONFIRMATION, {
                  params: {
                    title: i18next.t('commentbox.error.tooManyGifs.title'),
                    text: i18next.t('commentbox.error.tooManyGifs.text'),
                  },
                })
              );
              break;
            default:
              yield put(
                pushModal(ModalType.CONFIRMATION, {
                  params: {
                    title: i18next.t('commentbox.error.generic.title'),
                    text: i18next.t('common.somethingWentWrong'),
                  },
                })
              );
              break;
          }
        }
      }
    }
    if (result.errorDetails?.errorType !== 'media_content_violation') {
      yield putErrorNotification(
        i18next.t('comment.error.post', { error: getError(result).errorMsg })
      );
    }
    yield put(
      publishCommentFailure({
        editorId,
        isEdit: !!editComment,
        isReply: !!replyComment,
        shouldResetEditor,
        ...getError(result),
      })
    );
  }
}

export function* focusEditorWithScrollHandler({
  payload: { editorId, smoothScroll },
}: ReturnType<typeof focusEditorWithScroll>) {
  const editor = document.getElementById(editorId);
  const isMobile = yield select(getIsMobile);

  if (editor) {
    scrollIntoViewWithHeader(editor, isMobile, 100, undefined, smoothScroll);
    yield delay(500);
    yield put(focusEditor(editorId));
  }
}

export default function* saga(): any {
  yield takeLatest(publishComment, publishCommentHandler);
  yield takeLatest(focusEditorWithScroll, focusEditorWithScrollHandler);
}
